import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Documentation,
  DocumentationValidationStatusEnum,
  useEdmDocumentationBulkDestroyMutation,
  useEdmDocumentationBulkUpdateValidationStatusMutation,
  useEdmDocumentationUpdateMutation,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import toast from '@lib/components/Toast/Toast';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { useTabCountersContext } from '@lib/contexts/TabCountersContext';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import getAllTableItemsIds from '@lib/utils/getAllTableItemsIds';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import useEdmDocumentationsQueryHook from 'utils/fetch/hooks/useEdmDocumentationsQueryHook';
import { APP_URLS } from 'constants/urls';
import getDocumentsTableColumns from 'routes/Documentation/DocumentList/utils/getDocumentsTableColumns';
import getTableActionsMenu from 'routes/Documentation/DocumentList/utils/getTableActionsMenu';
import getTableActionsForSelected from 'routes/Documentation/DocumentList/utils/getTableActionsForSelected';
import FormTemplateBuilderDocumentsView from 'components/FormTemplateBuilder/FormTemplateBuilderView/FormTemplateBuilderDocumentsView';
import FormTemplateModals from './FormTemplateModals';
import { UseActionsResults } from './utils/useActions';
import { ModalManagementResult } from './utils/useModalManagement';

interface Props {
  useActionsResult: UseActionsResults;
  useModalManagementResult: ModalManagementResult;
  isViewOnly: boolean;
}

export default function FormTemplateDocumentsViewContainer(props: Props) {
  const { useActionsResult, useModalManagementResult, isViewOnly } = props;
  const { id = '' } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refetchCounters } = useTabCountersContext();

  const { openDestroyModal } = useModalManagementResult;

  const { getUserAccessByCompany }: ContextProps = useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const {
    formTemplateData,
    formTemplateFirstLoading,
    formTemplateLoading,
    formTemplateRefetch,
    formTemplateVersionData,
    loadingState,
    onCreateTaskHandler,
    onDuplicateHandler,
    onEditHandler,
    onFormTemplateArchiveHandler,
  } = useActionsResult;

  const [paginationParams] = usePaginationParams();

  // Bulk select for documents table
  const useBulkSelectResult = useTableItemsBulkSelect<Documentation>();

  // Documents query — skip entirely when EDM is locked (the BE filter
  // `linkedFormTemplatesId` is upgrade-feature-gated). For Full Admin we
  // render a lock-state SupportiveMessage instead of the table.
  const documentationsQueryHookResult = useEdmDocumentationsQueryHook({
    linkedFormTemplatesId: id,
    paginationParams,
    skip: isViewLocked,
  });

  const { collection, firstLoading, loading, totalCount, totalPages } =
    documentationsQueryHookResult;

  // Favorites
  const onFavoriteCompleted = useCallback(() => {
    // No favorites mode on this tab
  }, []);

  // Validation status
  const [bulkUpdateValidationStatus] =
    useEdmDocumentationBulkUpdateValidationStatusMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onValidationStatusChange = useCallback(
    (docId: string, newStatus: DocumentationValidationStatusEnum) => {
      bulkUpdateValidationStatus({
        variables: {
          ids: [docId],
          attributes: { validationStatus: newStatus },
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          documentationsQueryHookResult.refetch();
        },
      });
    },
    [bulkUpdateValidationStatus, documentationsQueryHookResult, t],
  );

  const onStatusBulkChangeHandler = useCallback(
    (newStatus: DocumentationValidationStatusEnum) => {
      tableItemsBulkAction<Documentation>({
        action: (ids) => {
          bulkUpdateValidationStatus({
            variables: {
              ids,
              attributes: { validationStatus: newStatus },
            },
            onCompleted: () => {
              toast({ content: t('update-document-success') });
              useBulkSelectResult.handleUnselectAllTableItems();
              documentationsQueryHookResult.refetch();
            },
          });
        },
        onGetAllIds: () =>
          getAllTableItemsIds<Documentation>({
            fetchIds: documentationsQueryHookResult.fetchIds,
            useBulkSelectResult,
          }),
        useBulkSelectResult,
      });
    },
    [
      bulkUpdateValidationStatus,
      documentationsQueryHookResult,
      t,
      useBulkSelectResult,
    ],
  );

  // Navigation
  const onOpen = useCallback(
    (item: Documentation) => {
      navigate(
        APP_URLS.dashboard.documentation.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  // Delete
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const isDeleteModalOpen = deleteIds.length > 0;

  const [destroyMutation, { loading: deleteLoading }] =
    useEdmDocumentationBulkDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onDelete = useCallback((item: Documentation) => {
    setDeleteIds([item.id]);
  }, []);

  const onBulkDelete = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setDeleteIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeDeleteModal = useCallback(() => {
    setDeleteIds([]);
  }, []);

  const onConfirmedDelete = useCallback(() => {
    if (!deleteIds.length) return;
    const idsToDelete = [...deleteIds];
    destroyMutation({
      variables: { ids: idsToDelete },
      onCompleted: () => {
        setDeleteIds([]);
        useBulkSelectResult.handleUnselectAllTableItems();
        toast({ content: t('destroy-documents-success') });
        onDestroyListItemCallBack({
          collection,
          collectionForDestroy: idsToDelete,
          navigate,
          pathname,
          refetch: () => {
            documentationsQueryHookResult.refetch();
            refetchCounters();
          },
          search,
          totalPages,
        });
      },
    });
  }, [
    collection,
    deleteIds,
    destroyMutation,
    documentationsQueryHookResult,
    navigate,
    pathname,
    refetchCounters,
    search,
    t,
    totalPages,
    useBulkSelectResult,
  ]);

  // Unlink
  const [unlinkDoc, setUnlinkDoc] = useState<Documentation | null>(null);

  const [updateMutation, { loading: unlinkLoading }] =
    useEdmDocumentationUpdateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onUnlink = useCallback((item: Documentation) => {
    setUnlinkDoc(item);
  }, []);

  const closeUnlinkModal = useCallback(() => {
    setUnlinkDoc(null);
  }, []);

  const onConfirmedUnlink = useCallback(() => {
    if (!unlinkDoc) return;
    updateMutation({
      variables: {
        id: unlinkDoc.id,
        attributes: {
          formTemplateLinks: [{ id, unlink: true }],
        },
      },
      onCompleted: () => {
        setUnlinkDoc(null);
        toast({ content: t('unlink-from-form-template-success') });
        documentationsQueryHookResult.refetch();
        refetchCounters();
      },
    });
  }, [
    documentationsQueryHookResult,
    id,
    refetchCounters,
    t,
    unlinkDoc,
    updateMutation,
  ]);

  // File viewer & version notes
  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  const closeFileViewer = useCallback(() => {
    setFileViewerDocument(null);
  }, []);

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const closeVersionNotes = useCallback(() => {
    setVersionNotesDocument(null);
  }, []);

  // Table config
  const tableColumns = useMemo(
    () =>
      getDocumentsTableColumns({
        onFavoriteCompleted,
        onOpen,
        onValidationStatusChange,
        t,
      }),
    [onFavoriteCompleted, onOpen, onValidationStatusChange, t],
  );

  const tableActionsMenu = useMemo(
    () =>
      getTableActionsMenu({
        onViewDocument,
        onVersionNotes,
        onDetails: (item) =>
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id: item.id },
            }),
          ),
        onEdit: isViewOnly
          ? undefined
          : (item) =>
              navigate(
                APP_URLS.dashboard.documentation.edit.getDynamicPath({
                  pathParts: { id: item.id },
                }),
              ),
        onUnlink: isViewOnly ? undefined : onUnlink,
        onDelete: isViewOnly ? undefined : onDelete,
        t,
        unlinkLabel: t('unlink-from-form-template'),
      }),
    [
      isViewOnly,
      navigate,
      onDelete,
      onUnlink,
      onVersionNotes,
      onViewDocument,
      t,
    ],
  );

  // Bulk edit
  const [bulkEditIds, setBulkEditIds] = useState<string[]>([]);
  const isBulkEditModalOpen = bulkEditIds.length > 0;

  const onBulkEdit = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setBulkEditIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeBulkEditModal = useCallback(() => {
    setBulkEditIds([]);
  }, []);

  const onBulkEditCompleted = useCallback(() => {
    closeBulkEditModal();
    documentationsQueryHookResult.refetch();
  }, [closeBulkEditModal, documentationsQueryHookResult]);

  const tableActionsForSelected = useMemo(
    () =>
      getTableActionsForSelected({
        onBulkEdit,
        onDelete: onBulkDelete,
        onStatusBulkChangeHandler,
        selectedItemsCount:
          useBulkSelectResult?.getSelectedItemsCount(totalCount),
        t,
      }),
    [
      onBulkDelete,
      onBulkEdit,
      onStatusBulkChangeHandler,
      totalCount,
      useBulkSelectResult,
      t,
    ],
  );

  if (formTemplateFirstLoading) {
    return <CircleLoader />;
  }

  return (
    <>
      <FormTemplateBuilderDocumentsView
        bulkEditIds={bulkEditIds}
        onBulkEditCompleted={onBulkEditCompleted}
        closeDeleteModal={closeDeleteModal}
        closeFileViewer={closeFileViewer}
        closeUnlinkModal={closeUnlinkModal}
        closeVersionNotes={closeVersionNotes}
        closeBulkEditModal={closeBulkEditModal}
        collection={collection}
        deleteLoading={deleteLoading}
        error={documentationsQueryHookResult.error}
        fileViewerDocument={fileViewerDocument}
        firstLoading={firstLoading}
        formTemplateData={formTemplateData}
        formTemplateId={id}
        formTemplateLoading={formTemplateLoading}
        formTemplateVersionData={formTemplateVersionData}
        isBulkEditModalOpen={isBulkEditModalOpen}
        isDeleteModalOpen={isDeleteModalOpen}
        isUnlinkModalOpen={!!unlinkDoc}
        isViewLocked={isViewLocked}
        isViewOnly={isViewOnly}
        loading={loading}
        refetchFormTemplate={formTemplateRefetch}
        loadingState={loadingState}
        onConfirmedDelete={onConfirmedDelete}
        onConfirmedUnlink={onConfirmedUnlink}
        onCreateTaskHandler={onCreateTaskHandler}
        onDuplicateHandler={onDuplicateHandler}
        onEditHandler={onEditHandler}
        onFormTemplateArchiveHandler={onFormTemplateArchiveHandler}
        openDestroyModal={openDestroyModal}
        tableActionsForSelected={tableActionsForSelected}
        tableActionsMenu={tableActionsMenu}
        tableColumns={tableColumns}
        totalCount={totalCount}
        unlinkLoading={unlinkLoading}
        useBulkSelectResult={useBulkSelectResult}
        versionNotesDocument={versionNotesDocument}
      />
      <FormTemplateModals
        useActionsResult={useActionsResult}
        useModalManagementResult={useModalManagementResult}
      />
    </>
  );
}
